import { useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import logoSmall from '@/assets/logo-small.png';
import logo from '@/assets/logo.png';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/lib/auth';

interface NavItem {
  label: string;
  href: string;
  icon: string;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

interface SidebarProps {
  collapsed?: boolean;
}

const navSections: NavSection[] = [
  {
    items: [
      { label: 'Dashboard', href: '/app', icon: 'bi-house' },
    ],
  },
  {
    title: 'Accounting',
    items: [
      { label: 'Chart of Accounts', href: '/app/accounts', icon: 'bi-list-ul' },
      { label: 'Journal Entries', href: '/app/journal', icon: 'bi-journal-text' },
      { label: 'Transactions', href: '/app/transactions', icon: 'bi-arrow-left-right' },
      { label: 'Reconciliation', href: '/app/reconciliation', icon: 'bi-check2-square' },
    ],
  },
  {
    title: 'Banking',
    items: [
      { label: 'Bank Accounts', href: '/app/bank-accounts', icon: 'bi-bank' },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { label: 'Inventory Items', href: '/app/inventory', icon: 'bi-box-seam' },
      { label: 'Valuation', href: '/app/reports/inventory-valuation', icon: 'bi-currency-dollar' },
    ],
  },
  {
    title: 'Reports',
    items: [
      { label: 'Trial Balance', href: '/app/reports/trial-balance', icon: 'bi-table' },
      { label: 'Balance Sheet', href: '/app/reports/balance-sheet', icon: 'bi-file-earmark-spreadsheet' },
      { label: 'Profit & Loss', href: '/app/reports/profit-loss', icon: 'bi-graph-up' },
      { label: 'Tax Summary', href: '/app/reports/tax-summary', icon: 'bi-receipt' },
    ],
  },
  {
    title: 'Settings',
    items: [
      { label: 'Tax Codes', href: '/app/tax-codes', icon: 'bi-percent' },
    ],
  },
];

function NavLinkItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const getClassName = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-2 mx-2 rounded-lg transition-colors ${
      isActive
        ? 'bg-[var(--color-sidebar-primary)] text-white'
        : 'hover:bg-[var(--color-sidebar-accent)]'
    } ${collapsed ? 'justify-center' : ''}`;

  return (
    <NavLink
      to={item.href}
      className={getClassName}
      title={collapsed ? item.label : undefined}
    >
      <i className={`bi ${item.icon} ${collapsed ? 'text-lg' : ''}`}></i>
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const { user, signOut } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();

  const handleSignOut = useCallback(() => {
    void signOut();
    void navigate('/login');
  }, [signOut, navigate]);

  return (
    <div className="h-full flex flex-col">
      {/* Brand */}
      <div className={`border-b border-[var(--color-sidebar-border)] ${collapsed ? 'p-3' : 'p-5'}`}>
        <div className={`font-semibold ${collapsed ? 'text-center' : 'flex items-center gap-3'}`}>
          <img 
            src={collapsed ? logo : logoSmall} 
            alt="BeanGo" 
            className={collapsed ? 'h-8 w-auto mx-auto' : 'h-8 w-auto'} 
          />
        </div>
      </div>

      {/* Organization Name */}
      {!collapsed && organization && (
        <div className="border-b border-[var(--color-sidebar-border)] px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
            Organization
          </div>
          <div className="font-medium truncate" style={{ color: 'var(--color-sidebar-foreground)' }}>
            {organization.name}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navSections.map((section) => (
          <div key={section.title ?? 'main'} className="mb-4">
            {section.title && !collapsed && (
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
                {section.title}
              </div>
            )}
            {section.items.map((item) => (
              <NavLinkItem key={item.href} item={item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {/* User & Logout */}
      <div className="border-t border-[var(--color-sidebar-border)] px-4 py-3">
        {!collapsed && user && (
          <div className="mb-3 text-sm" style={{ color: 'var(--color-sidebar-foreground)' }}>
            <div className="font-medium truncate">{user.email}</div>
          </div>
        )}
        <button
          type="button"
          onClick={handleSignOut}
          className={`flex items-center gap-3 px-4 py-2 mx-2 rounded-lg transition-colors hover:bg-[var(--color-sidebar-accent)] w-[calc(100%-1rem)] ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Sign out' : undefined}
        >
          <i className={`bi bi-box-arrow-left ${collapsed ? 'text-lg' : ''}`}></i>
          {!collapsed && <span>Sign out</span>}
        </button>
        {!collapsed && (
          <div className="mt-2 text-xs" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
            v1.0.0
          </div>
        )}
      </div>
    </div>
  );
}